<template>
  <div ref="pageRoot" class="history-trace-container">
    <SearchCard v-model="queryForm" @handleQuery="handleQuery"></SearchCard>
    <div class="main-content">
      <section class="trace-list trace-list-panel">
        <div class="nx-card-header list-header-inner">
          <div class="nx-card-title">轨迹列表</div>
          <div class="nx-card-extra">
            <span class="list-count">共 {{ traceList.length }} 条记录</span>
          </div>
        </div>
        <div v-if="!traceList.length" class="trace-empty">暂无工单记录</div>
        <div class="trace-simple-list">
          <div
            class="trace-item"
            :class="{ activate: activate === index }"
            v-for="(item, index) in traceList"
            :key="item.id || item.workOrderId || index"
            @click="traceClick(item, index)"
          >
            <div class="trace-header">
              <div class="name">{{ item.workOrderCode || "—" }}</div>
              <div class="status-tag" :class="statusClass(item.orderStatus)">{{ getOrderStatusText(item.orderStatus) }}</div>
            </div>
            <div class="department">{{ item.extPlanName || "—" }} / {{ item.inspectionRouteName || "—" }}</div>
            <div class="time-info">
              <div class="time-item">
                <span class="label">工单开始</span>
                <span class="value">{{ displayStartTime(item) }}</span>
              </div>
              <div class="time-item">
                <span class="label">工单结束</span>
                <span class="value">{{ displayEndTime(item) }}</span>
              </div>
            </div>
            <div class="stats-info">
              <div class="stat-item">
                <i class="el-icon-user"></i>
                <span class="stat-text">人员: {{ item.userName || '—' }}</span>
              </div>
              <div class="stat-item">
                <i class="el-icon-location-outline"></i>
                <span class="stat-text">工单点位: {{ workOrderPointCount(item) }}</span>
              </div>
              <div class="stat-item">
                <i class="el-icon-map-location"></i>
                <span class="stat-text">轨迹点: {{ item.trackPointCount || 0 }}</span>
              </div>
              <div class="stat-item">
                <i class="el-icon-time"></i>
                <span class="stat-text">时长: {{ formatDuration(item.durationMinutes) }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div class="map-section">
        <div class="map-section-head">
          <div class="nx-card-title map-section-title">地图</div>
          <div class="map-summary">
            <span>工单：{{ activeTraceDetail.workOrderCode || '—' }}</span>
            <span>轨迹点：{{ activePath.length }}</span>
          </div>
        </div>
        <TiandiMap
          ref="traceMap"
          class="map-container"
          height="100%"
          :path-points="activePath"
          :moving-point="currentMovingPoint"
          :auto-fit="activePath.length > 0"
          :initial-markers="[]"
          :show-path-markers="false"
          :show-toolbar="false"
        ></TiandiMap>
        <div class="play-controls">
          <div class="play-controls-top">
            <div class="play-btns">
              <button type="button" class="play-btn skip" @click="playPrev" aria-label="上一帧">
                <i class="el-icon-d-arrow-left"></i>
              </button>
              <button
                type="button"
                class="play-btn play-main"
                :class="{ playing: isPlaying }"
                @click="togglePlay"
                aria-label="播放暂停"
              >
                <i class="el-icon-video-play" v-if="!isPlaying"></i>
                <i class="el-icon-video-pause" v-else></i>
              </button>
              <button type="button" class="play-btn skip" @click="playNext" aria-label="下一帧">
                <i class="el-icon-d-arrow-right"></i>
              </button>
            </div>
            <div class="play-speed">
              <span class="speed-label">播放速度</span>
              <el-select v-model="playSpeed" placeholder="1x" size="small" class="speed-select">
                <el-option label="0.5x" value="0.5"></el-option>
                <el-option label="1x" value="1"></el-option>
                <el-option label="2x" value="2"></el-option>
              </el-select>
            </div>
          </div>
          <div class="progress-bar">
            <el-slider
              v-model="playProgress"
              :max="totalPoints"
              :disabled="totalPoints <= 0"
              @change="handleProgressChange"
            ></el-slider>
          </div>
        </div>
      </div>

      <TraceDetail :detail="activeTraceDetail"></TraceDetail>
    </div>
  </div>
</template>

<script>
import SearchCard from './components/search-card'
import TraceDetail from './components/trace-detail'
import { getTrackGroupPage, getTrackPoints } from './api'
import TiandiMap from '@/components/tiandi-map/index.vue'

export default {
  name: 'HistoryTrace',
  components: {
    SearchCard,
    TraceDetail,
    TiandiMap
  },
  data () {
    return {
      queryForm: {},
      activate: 0,
      traceList: [],
      activeTraceId: null,
      activeTraceDetail: {},
      activePath: [],
      isPlaying: false,
      playProgress: 0,
      playSpeed: '1',
      totalPoints: 0,
      playTimer: null
    }
  },
  computed: {
    currentMovingPoint () {
      if (!this.activePath.length) return null
      return this.activePath[Math.min(this.playProgress, this.activePath.length - 1)]
    }
  },
  mounted () {
    this._layoutOnResize = () => this.syncLayoutHeight()
    window.addEventListener('resize', this._layoutOnResize)
    this.$nextTick(() => {
      this.syncLayoutHeight()
      const shell = typeof document !== 'undefined' ? document.getElementById('avue-view') : null
      if (shell && typeof ResizeObserver !== 'undefined') {
        this._layoutRo = new ResizeObserver(() => this.syncLayoutHeight())
        this._layoutRo.observe(shell)
      }
      this.getList()
    })
  },
  beforeDestroy () {
    this.stopPlay()
    if (this._layoutOnResize) {
      window.removeEventListener('resize', this._layoutOnResize)
      this._layoutOnResize = null
    }
    if (this._layoutRo) {
      try {
        this._layoutRo.disconnect()
      } catch (e) {
        console.warn('layout observer disconnect failed:', e)
      }
      this._layoutRo = null
    }
    const root = this.$refs.pageRoot
    if (root && root.style) {
      root.style.height = ''
    }
  },
  methods: {
    /** 与 #avue-view 内容区等高，避免整页超出主区域产生外层滚动；内部列表仍可在 .trace-simple-list 内滚动 */
    syncLayoutHeight () {
      this.$nextTick(() => {
        const shell = typeof document !== 'undefined' ? document.getElementById('avue-view') : null
        const el = this.$refs.pageRoot
        if (!shell || !el) return
        const cs = window.getComputedStyle(shell)
        const pt = parseFloat(cs.paddingTop) || 0
        const pb = parseFloat(cs.paddingBottom) || 0
        const h = Math.max(200, shell.clientHeight - pt - pb)
        el.style.height = `${h}px`
      })
    },
    formatDistance (val) {
      if (val == null || val === '') return '—'
      const n = Number(val)
      if (Number.isNaN(n)) return String(val)
      return `${(n / 1000).toFixed(1)} km`
    },
    formatDuration (val) {
      if (val == null || val === '') return '—'
      const n = Number(val)
      if (Number.isNaN(n)) return '—'
      if (n < 60) return `${n}分钟`
      const h = Math.floor(n / 60)
      const m = n % 60
      return m > 0 ? `${h}小时${m}分钟` : `${h}小时`
    },
    displayStartTime (row) {
      if (!row) return '—'
      return row.workOrderStartTime || row.actualStartTime || row.cycleStartTime || row.createTime || '—'
    },
    displayEndTime (row) {
      if (!row) return '—'
      return row.workOrderEndTime || row.actualEndTime || row.cycleEndTime || row.updateTime || '—'
    },
    workOrderPointCount (row) {
      if (!row) return 0
      const planCount = row.planPointCount != null ? Number(row.planPointCount) : null
      const completedCount = row.completedPointCount != null ? Number(row.completedPointCount) : null
      const extCount = row.extPointCount != null ? Number(row.extPointCount) : 0
      if (planCount != null && !Number.isNaN(planCount)) {
        if (completedCount != null && !Number.isNaN(completedCount)) {
          return `${completedCount}/${planCount}`
        }
        return planCount
      }
      return Number.isNaN(extCount) ? 0 : extCount
    },
    statusClass (status) {
      if (status === 1) return 'running'
      if (status === 2) return 'finished'
      return 'default'
    },
    getOrderStatusText (status) {
      const map = {
        0: '待执行',
        1: '执行中',
        2: '已完成',
        3: '已超时',
        4: '超时完成',
        5: '已取消',
        6: '未执行'
      }
      return map[status] || '未知'
    },
    normalizePath (raw) {
      if (raw == null) return []
      if (Array.isArray(raw)) {
        if (!raw.length) return []
        return raw
          .map((p) => {
            if (Array.isArray(p) && p.length >= 2) {
              return [Number(p[0]), Number(p[1])]
            }
            if (p && typeof p === 'object') {
              const lng = p.lng != null ? p.lng : p.longitude
              const lat = p.lat != null ? p.lat : p.latitude
              if (lng != null && lat != null) return [Number(lng), Number(lat)]
            }
            return null
          })
          .filter((x) => x && !Number.isNaN(x[0]) && !Number.isNaN(x[1]))
      }
      if (typeof raw === 'string') {
        try {
          return this.normalizePath(JSON.parse(raw))
        } catch (e) {
          return []
        }
      }
      return []
    },
    traceClick (item, index) {
      this.activate = index
      this.activeTraceId = item.id
      this.loadTraceDetail(item)
    },
    getList (queryForm = {}) {
      const params = this.normalizeQueryParams(queryForm)
      getTrackGroupPage(params).then(({ data }) => {
        const page = data.data || {}
        this.traceList = page.records || []
        if (this.traceList.length) {
          this.activate = 0
          this.activeTraceId = this.traceList[0].id
          this.loadTraceDetail(this.traceList[0])
        } else {
          this.activeTraceId = null
          this.activeTraceDetail = {}
          this.activePath = []
          this.playProgress = 0
          this.totalPoints = 0
          this.stopPlay()
        }
      })
    },
    normalizeQueryParams (queryForm) {
      const params = {
        ...queryForm,
        page: 1,
        size: 100
      }
      if (params.startTime && Array.isArray(params.startTime)) {
        const range = params.startTime
        params.startTime = range[0]
        params.endTime = range[1]
      }
      Object.keys(params).forEach((key) => {
        if (params[key] === '' || params[key] == null) {
          delete params[key]
        }
      })
      return params
    },
    handleQuery () {
      this.getList(this.queryForm)
    },
    loadTraceDetail (row) {
      if (!row || !row.workOrderId) return
      this.activeTraceDetail = {
        ...row,
        trackPoints: [],
        path: []
      }
      const startTime = row.trackStartTime || this.queryForm.startTime
      const endTime = row.trackEndTime || this.queryForm.endTime
      getTrackPoints({
        workOrderId: row.workOrderId,
        startTime,
        endTime
      }).then(({ data }) => {
        const pointList = data.data || []
        this.activeTraceDetail = {
          ...row,
          trackPoints: pointList,
          path: pointList.map((item) => ({
            longitude: item.longitude,
            latitude: item.latitude
          }))
        }
        this.activePath = this.normalizePath(this.activeTraceDetail.path)
        this.playProgress = 0
        this.totalPoints = Math.max(0, this.activePath.length - 1)
        this.stopPlay()
      })
    },
    togglePlay () {
      this.isPlaying ? this.stopPlay() : this.startPlay()
    },
    startPlay () {
      if (this.totalPoints <= 0) return
      this.isPlaying = true
      const speed = 1000 / Number(this.playSpeed)
      this.playTimer = setInterval(() => {
        if (this.playProgress >= this.totalPoints) {
          this.stopPlay()
          return
        }
        this.playProgress += 1
        this.updateMarkerPosition()
      }, speed)
    },
    stopPlay () {
      this.isPlaying = false
      if (this.playTimer) {
        clearInterval(this.playTimer)
        this.playTimer = null
      }
    },
    playPrev () {
      this.playProgress = Math.max(0, this.playProgress - 1)
      this.updateMarkerPosition()
    },
    playNext () {
      this.playProgress = Math.min(this.totalPoints, this.playProgress + 1)
      this.updateMarkerPosition()
    },
    updateMarkerPosition () {
      if (!this.activePath.length) return
    },
    handleProgressChange (val) {
      this.playProgress = val
      this.updateMarkerPosition()
    }
  }
}
</script>

<style scoped lang="scss">
/* 本页专用设计 token（原 @/styles/nx-tokens.scss，仅本页使用） */
$nx-color-bg: #f5f7fa;
$nx-color-card: #fff;
$nx-color-text-1: #1f2937;
$nx-color-text-2: #6b7280;
$nx-color-text-3: #909399;
$nx-color-primary: #2563eb;
$nx-color-border: #e5e7eb;
$nx-color-border-2: #ebeef5;
/* 卡片圆角与 Element el-card（4px）一致；本页未使用 el-card，自定义面板对齐该值 */
$nx-radius-sm: 4px;
$nx-radius-md: 4px;
$nx-radius-lg: 4px;
$nx-shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.05);
$nx-space-1: 4px;
$nx-space-2: 8px;
$nx-space-3: 12px;
$nx-space-4: 16px;
$nx-space-5: 20px;
$nx-space-6: 24px;

@mixin nx-surface-card {
  background: $nx-color-card;
  border: 1px solid $nx-color-border-2;
  border-radius: $nx-radius-lg;
  box-shadow: $nx-shadow-sm;
  box-sizing: border-box;
}

.history-trace-container {
  font-family: 'Microsoft YaHei', 'PingFang SC', sans-serif;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  /* 与下方主区间距由 SearchCard 的 margin-bottom 承担，对齐 .avue-crud__search */
  gap: 0;
}

.main-content {
  display: flex;
  flex: 1;
  min-height: 0;
  gap: $nx-space-3;
  box-sizing: border-box;
}

.trace-list-panel {
  @include nx-surface-card;
  width: 360px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding-bottom: $nx-space-3;
}

.list-header-inner {
  flex-shrink: 0;
  padding: $nx-space-4 $nx-space-5 0;
  margin-bottom: 0;
}

.list-header-inner.nx-card-header {
  border-bottom: 1px solid $nx-color-border-2;
  padding-bottom: $nx-space-3;
  margin-bottom: $nx-space-3;
}

.list-count {
  font-size: 13px;
  font-weight: 400;
  color: $nx-color-text-3;
}

.trace-empty {
  flex: 1;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $nx-color-text-3;
  font-size: 14px;
  padding: $nx-space-6;
}

.trace-simple-list {
  flex: 1;
  overflow-y: auto;
  padding: 0 $nx-space-5;
  margin-right: 2px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.12);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
}

.trace-item {
  background: $nx-color-bg;
  border-radius: $nx-radius-md;
  padding: $nx-space-3 $nx-space-4;
  margin-bottom: $nx-space-3;
  cursor: pointer;
  border: 1px solid transparent;
  transition: box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease;

  &:last-child {
    margin-bottom: 0;
  }
}

.trace-item:hover {
  background: #fff;
  box-shadow: $nx-shadow-sm;
  border-color: $nx-color-border-2;
}

.trace-item.activate {
  background: #fff;
  border-color: rgba(37, 99, 235, 0.35);
  box-shadow: 0 0 0 1px rgba(37, 99, 235, 0.2);
}

.trace-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: $nx-space-2;
}

.name {
  font-size: 15px;
  font-weight: 600;
  color: $nx-color-text-1;
  line-height: 1.4;
}

.status-tag {
  padding: 2px 10px;
  border-radius: $nx-radius-sm;
  font-size: 12px;
  font-weight: 500;
  flex-shrink: 0;
  line-height: 1.4;
}

.status-tag.running {
  background-color: #eff6ff;
  color: $nx-color-primary;
}

.status-tag.finished {
  background-color: #ecfdf3;
  color: #16a34a;
}

.status-tag.default {
  background-color: #f4f4f5;
  color: #52525b;
}

.department {
  font-size: 13px;
  color: $nx-color-text-2;
  margin-bottom: $nx-space-3;
  line-height: 1.45;
}

.time-info {
  display: flex;
  flex-direction: column;
  gap: $nx-space-2;
  margin-bottom: $nx-space-3;
}

.time-item {
  font-size: 12px;
  display: flex;
  align-items: baseline;
  gap: $nx-space-2;
}

.time-item .label {
  color: $nx-color-text-3;
  flex-shrink: 0;
}

.time-item .value {
  color: $nx-color-text-1;
  font-variant-numeric: tabular-nums;
}

.stats-info {
  display: flex;
  flex-wrap: wrap;
  gap: $nx-space-2 $nx-space-4;
  padding-top: $nx-space-3;
  border-top: 1px solid $nx-color-border;
}

.stats-info .stat-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: $nx-color-text-2;
  padding: 4px 10px;
  background: #fff;
  border-radius: $nx-radius-sm;
  border: 1px solid $nx-color-border;
}

.stats-info .stat-item i {
  color: $nx-color-text-3;
  font-size: 14px;
}

.stat-text {
  color: $nx-color-text-1;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
}

.map-section {
  @include nx-surface-card;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
}

.map-section-head {
  flex-shrink: 0;
  padding: $nx-space-4 $nx-space-5 $nx-space-3;
  border-bottom: 1px solid $nx-color-border-2;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
}

.map-section-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  color: $nx-color-text-1;
}

.map-summary {
  display: flex;
  gap: 16px;
  color: $nx-color-text-3;
  font-size: 13px;
  flex-wrap: wrap;
}

.map-container {
  flex: 1;
  min-height: 320px;
  width: 100%;
  background: #e8ecf0;
}

.play-controls {
  flex-shrink: 0;
  padding: $nx-space-3 $nx-space-5 $nx-space-4;
  background: $nx-color-card;
  border-top: 1px solid $nx-color-border-2;
}

.play-controls-top {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: $nx-space-6;
  margin-bottom: $nx-space-2;
  position: relative;
  min-height: 52px;
}

.play-btns {
  display: flex;
  align-items: center;
  gap: $nx-space-3;
}

.play-btn {
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease, transform 0.1s ease, box-shadow 0.2s ease;
}

.play-btn:active {
  transform: scale(0.96);
}

.play-btn.skip {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #eff6ff;
  color: $nx-color-primary;
  border: 1px solid rgba(37, 99, 235, 0.15);
}

.play-btn.skip:hover {
  background: #dbeafe;
}

.play-main {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: $nx-color-primary;
  color: #fff;
  font-size: 22px;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.35);
}

.play-main:hover {
  filter: brightness(1.06);
}

.play-main.playing {
  background: #16a34a;
  box-shadow: 0 4px 12px rgba(22, 163, 74, 0.35);
}

.play-main.playing:hover {
  filter: brightness(1.06);
}

.play-speed {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: $nx-space-2;
  font-size: 13px;
  color: $nx-color-text-2;
}

.speed-label {
  white-space: nowrap;
}

.speed-select {
  width: 92px;
}

.progress-bar {
  width: 100%;
}

.progress-bar ::v-deep .el-slider__runway {
  margin: $nx-space-3 0 $nx-space-1;
  height: 6px;
  border-radius: 3px;
  background-color: #e5e7eb;
}

.progress-bar ::v-deep .el-slider__bar {
  border-radius: 3px;
  background: linear-gradient(90deg, $nx-color-primary, #3b82f6);
}

.progress-bar ::v-deep .el-slider__button {
  width: 14px;
  height: 14px;
  border: 2px solid $nx-color-primary;
}

@media (max-width: 1200px) {
  .play-speed {
    position: static;
    transform: none;
    margin-top: $nx-space-3;
    justify-content: flex-end;
    width: 100%;
  }

  .play-controls-top {
    flex-wrap: wrap;
  }

  .trace-list-panel {
    width: 300px;
  }
}
</style>
